<template>
  <div class="container">
    <DataGetter
      :url="`${apiRoute}/list/countryISO`"
      :params="{ indicator: 'Google index' }"
      @fetched="setCountries"
    />
    <Block title="Tracking Google searches for energy-related terms">
      <p>
        The data provided is anonymised, categorised (determining the topic for
        a search query) and aggregated. Topics within Google Trends are language
        neutral which allows searches across countries without having to deal
        with translation issues. This allows us to display global interest in a
        particular topic.<ReferenceModal :refIndex="1">
          <p>Google Trends normalizes data by the following process:</p>
          <ul>
            <li>
              Each data point is divided by the total searches of the geography
              and time range it represents to compare relative popularity.
            </li>
            <li>
              The resulting numbers are then scaled on a range of 0 to 100 based
              on each topic&apos;s proportion within all searches on all topics.
            </li>
            <li>
              Different countries that show the same search interest for a term
              don't always have the same total search volumes.
            </li>
          </ul>
          <p>
            For more information, please visit
            <a
              href="https://support.google.com/trends/answer/4365533?hl=en-GB&ref_topic=6248052"
              >Google Trends FAQ</a
            >.
          </p>
        </ReferenceModal>
      </p>
      <p>You can visualise search volumes in two different ways:</p>
      <ul>
        <li>
          <strong>Total search volume</strong> shows the proportion of searches
          for a given keyword in a given country relative to a fixed reference
          point.<ReferenceModal :refIndex="2"
            ><p>
              Search volumes are indexed to the proprtion of searches for "wind
              power" in the United States in January 2010, which equals 100. For
              example, the index of "solar power" searches in France in January
              2010 was 120 meaning the proporation of "solar" searches in France
              was 20% higher the proportion of "wind power" searches in the
              United States in the same period.
            </p></ReferenceModal
          >
          This allows comparison of relative interest in energy topics within or
          across countries.
        </li>
        <li>
          <strong>Growth over time</strong> indexes search volumes in each
          country or term to the selected start date, allowing comparison of the
          <em>growth</em> in interest in different energy topics.
        </li>
      </ul>
    </Block>
    <div class="google__layout">
      <div class="google__sidebar" v-show="countries.length > 0">
        <ControlGroup>
          <Dropdown
            label="Compare"
            :options="['Countries', 'Search terms']"
            v-model="mode"
          />
          <DatePicker
            :min="minMaxYears[0]"
            :max="minMaxYears[1]"
            v-model="years"
            view-type="month"
          />
        </ControlGroup>
        <ControlGroup>
          <Dropdown
            v-if="mode === 'Search terms'"
            label="Country/region"
            :options="countries"
            option-label="name"
            option-value="iso3"
            v-model="selectedCountry"
          />
          <Dropdown
            v-if="mode === 'Countries'"
            label="Search term"
            :options="termList"
            v-model="selectedTerm"
          />
        </ControlGroup>
        <ControlGroup>
          <Dropdown
            label="Show"
            small
            :options="[
              { label: 'Total search volume', value: false },
              { label: 'Growth over time', value: true },
            ]"
            v-model="indexByStart"
          />
        </ControlGroup>
        <SelectList
          v-if="mode === 'Countries'"
          :options="countries"
          option-label="name"
          option-value="iso3"
          seperated
          v-model="selectedCountries"
        />
        <SelectList
          v-if="mode === 'Search terms'"
          :options="termList"
          seperated
          v-model="selectedTerms"
        />
      </div>
      <div class="google__main">
        <DataGetter :url="apiRoute" :params="params" v-slot="{ data }" lazy>
          <DataSlicer
            :data="processYears(data)"
            x-axis-dimension="year"
            :y-axis-dimension="mode === 'Countries' ? 'country' : 'techSector'"
            value-dimension="value"
            v-slot="{ slicedData }"
          >
            <ChartLayout
              :title="
                mode === 'Countries'
                  ? `Search volume for &quot;${selectedTerm}&quot; by country`
                  : `Search volume in ${getCountryNameByISO(
                      selectedCountry
                    )} by term`
              "
              :units="`Index, 100 = ${
                indexByStart
                  ? 'search volume on starting date'
                  : 'reference value'
              }`"
            >
              <Chart
                v-if="
                  mode === 'Countries'
                    ? selectedCountries.length > 0
                    : selectedTerms.length > 0
                "
                :options="{
                  ...chartOptions,
                  series: indexByStart
                    ? indexToStartYear(slicedData)
                    : indexToVolume(slicedData),
                }"
              />
              <span class="no-data" v-else
                >Please select at least one
                {{ mode === "Countries" ? "country" : "term" }}.</span
              >
              <template #notes>
                <p>Search volumes include related terms and other languages.</p>
                <p v-if="!indexByStart">
                  Reference value is the search volume for the term "Wind power"
                  in the United States in January 2010.
                </p>
              </template>
            </ChartLayout>
          </DataSlicer>
        </DataGetter>
      </div>
    </div>
  </div>
</template>

<script>
import {
  ControlGroup,
  Dropdown,
  SelectList,
  DatePicker,
  Block,
  ReferenceModal,
  //Toggle,
  DataGetter,
  DataSlicer,
} from "@international-energy-agency/vue-ui";
import { Chart } from "highcharts-vue";
import ChartOptions, {
  ChartLayout,
} from "@international-energy-agency/vue-ui/highcharts";
const termsByCategory = {
  Transport: [
    "Car",
    "Electric scooter",
    "Electricity",
    "Electric vehicle",
    "Batteries",
    "Sport utility vehicle",
    "Train",
    "Bicycle",
    "Public transport",
    "Electric bicycle",
    "Flight",
    "Charging stations",
    "Hybrid vehicle",
  ],
  "Home appliance": [
    "Air conditioner",
    "Home automation",
    "Ventilation",
    "Washing machine",
    "Freezer",
    "Induction range",
    "Clothes dryer",
    "Dishwasher",
    "Boiler",
    "LED lamp",
    "Refrigerator",
    "Heat pump",
  ],
  "Energy source": [
    "LNG",
    "Electrification",
    "Petroleum",
    "Solar power",
    "Coal",
    "Natural gas",
    "Fuel cell",
    "Hydrogen",
    "Nuclear",
    "Hydroelectricity",
    "Wind power",
    "Oil",
    "Geothermal",
    "Offshore wind",
    "Carbon capture and storage",
  ],
};
export default {
  props: {
    apiRoute: {
      type: String,
      default: "https://api.iea.org/h2020",
    },
  },
  components: {
    ControlGroup,
    Dropdown,
    SelectList,
    Block,
    ReferenceModal,
    //Toggle,
    DatePicker,
    DataGetter,
    DataSlicer,
    ChartLayout,
    Chart,
  },
  data() {
    const years = [
      new Date(Date.UTC(2010, 0, 1)).getTime(),
      new Date(Date.UTC(2021, 11, 1)).getTime(),
    ];

    return {
      chartOptions: ChartOptions.buildOptions(
        {
          xAxis: {
            type: "datetime",
          },
          tooltip: {
            xDateFormat: "%B %Y",
          },
          yAxis: {
            className: "highcharts-show-grid",
          },
        },
        "line"
      ),
      mode: "Countries",
      indexByStart: false,
      minMaxYears: years,
      years,
      countries: [],
      selectedCountries: ["USA"],
      selectedCountry: "USA",
      selectedTerms: ["Wind power"],
      selectedTerm: "Wind power",
      terms: Object.keys(termsByCategory).reduce(
        (terms, category) => [...terms, ...termsByCategory[category]],
        []
      ),
      termList: Object.keys(termsByCategory).reduce(
        (termList, category) => [
          ...termList,
          { label: category, heading: true, disabled: true },
          ...termsByCategory[category].map((term) => ({
            label: term,
            value: term,
          })),
        ],
        []
      ),
    };
  },
  watch: {
    selectedCountries(newCountries) {
      this.selectedCountry = newCountries[0];
    },
    selectedTerms(newTerms) {
      this.selectedTerm = newTerms[0];
    },
  },
  computed: {
    params() {
      const {
        mode,
        selectedCountries,
        selectedCountry,
        selectedTerm,
        selectedTerms,
      } = this;
      return {
        indicator: "Google index",
        countryISO: mode === "Countries" ? selectedCountries : selectedCountry,
        techSector: mode === "Search terms" ? selectedTerms : selectedTerm,
        allYears: true,
      };
    },
  },
  methods: {
    getCountryNameByISO(iso) {
      return this.countries.find((country) => country.iso3 === iso)?.name;
    },
    getISObyCountryName(name) {
      return this.countries.find(
        (country) => country.name === name || country.fullName === name
      )?.iso3;
    },
    getDate(yearMonth) {
      return new Date(
        Date.UTC(
          `20${yearMonth.toString().slice(0, 2)}`, // year
          yearMonth.toString().slice(2) - 1, // month
          1 // day
        )
      ).getTime();
    },
    getSeriesColor(seriesName) {
      const { mode, selectedTerms, selectedCountries } = this;
      const rawIndex =
        mode === "Countries"
          ? selectedCountries.findIndex(
              (iso) => iso === this.getISObyCountryName(seriesName)
            )
          : selectedTerms.indexOf(seriesName);
      return rawIndex % 10;
    },
    indexToVolume(series) {
      const { years } = this;
      return series.map((series) => {
        const dataRange = series.data.filter(
          (point) => point[0] >= years[0] && point[0] <= years[1]
        );
        return {
          ...series,
          colorIndex: this.getSeriesColor(series.name),
          data: dataRange.map((point) => [point[0], point[1] * 100]),
        };
      });
    },
    indexToStartYear(series) {
      const { years } = this;
      return series.map((series) => {
        const dataRange = series.data.filter(
          (point) => point[0] >= years[0] && point[0] <= years[1]
        );
        const base = dataRange[0][1] || 1;
        return {
          ...series,
          colorIndex: this.getSeriesColor(series.name),
          data: dataRange.map((point) => [point[0], (point[1] / base) * 100]),
        };
      });
    },
    indexToLowest(series) {
      const base = series.reduce(
        (lowest, series) =>
          series.data[0][1] && series.data[0][1] < lowest
            ? series.data[0][1]
            : lowest,
        1
      );
      return series.map((series) => ({
        ...series,
        data: series.data.map((point) => [point[0], (point[1] / base) * 100]),
      }));
    },
    processYears(data) {
      return data.map((row) => ({ ...row, year: this.getDate(row.year) }));
    },
    setCountries(countries) {
      this.countries = countries;
    },
  },
};
</script>

<style>
.google__layout {
  display: flex;
}
@media screen and (max-width: 729px) {
  .google__layout {
    display: block;
  }
}
.google__sidebar {
  position: relative;
  min-width: 30%;
  display: flex;
  flex-direction: column;
  max-height: 800px;
}
.google__main {
  padding-left: 24px;
  flex: 1 0 auto;
}
</style>
